import type { StructuredContentDocument } from '@repo/contracts/multi-use/structured-content/types';
import { css } from '@repo/pandacss/styled-system/css';
import Image from 'next/image';
import { StructuredContentRenderer } from '../../../StructuredContentRenderer';
import { SPOTLIGHT_BANNER_SVG } from './constants';

export type SpotlightBannerProps = {
  title?: string;
  details?: StructuredContentDocument;
};

const SPOTLIGHT_LABEL = 'Why this account stands out';

const bannerContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: 'nwGreen.snowyMint',
  borderTopWidth: 'thin',
  borderRightWidth: 'thin',
  borderBottomWidth: 'thin',
  borderLeftWidth: 'thin',
  borderTopStyle: 'solid',
  borderRightStyle: 'solid',
  borderBottomStyle: 'solid',
  borderLeftStyle: 'solid',
  borderTopColor: 'nwGreen.mossGreen',
  borderRightColor: 'nwGreen.mossGreen',
  borderBottomColor: 'nwGreen.mossGreen',
  borderLeftColor: 'nwGreen.mossGreen',
  borderTopLeftRadius: 'sm',
  borderTopRightRadius: 'sm',
  borderBottomRightRadius: 'sm',
  borderBottomLeftRadius: 'sm',
  paddingTop: '4',
  paddingRight: '4',
  paddingBottom: '4',
  paddingLeft: '4',
  width: 'full',
  position: 'relative',
  marginBottom: { base: '2', md: '3' },
});

const bannerContentStyles = css({
  display: { base: 'grid', md: 'flex' },
  gridTemplateColumns: { base: '[auto 1fr]' },
  rowGap: { base: '2', md: '4' },
  columnGap: { md: '4' },
  alignItems: 'center',
});

const bannerImageStyles = css({
  width: { base: '10', md: 'auto' },
  height: { base: '6', md: 'auto' },
  alignSelf: 'center',
  gridColumnStart: { base: '1' },
  gridColumnEnd: { base: 'auto' },
  marginRight: '2',
  flexShrink: '0',
});

const bannerTextWrapperStyles = css({
  display: { base: 'contents', md: 'flex' },
  flexDirection: { md: 'column' },
  columnGap: { md: '1' },
  rowGap: { md: '1' },
});

const bannerLabelStyles = css({
  fontWeight: 'medium',
  fontSize: 'md',
  lineHeight: 'normal',
  color: 'neutral.darker',
  gridColumnStart: { base: '[2]', md: 'unset' },
  gridColumnEnd: { base: 'auto', md: 'unset' },
});

const bannerBodyStyles = css({
  fontSize: 'md',
  lineHeight: 'normal',
  fontWeight: 'light',
  color: 'neutral.darker',
  gridColumnStart: { base: '[span 2]', md: 'unset' },
  gridColumnEnd: { base: 'auto', md: 'unset' },
});

export const SpotlightBanner = ({ title, details }: SpotlightBannerProps) => {
  if (!title || !details) {
    return null;
  }

  return (
    <div className={bannerContainerStyles} data-testid="spotlight-banner">
      <div className={bannerContentStyles}>
        <Image
          src={SPOTLIGHT_BANNER_SVG}
          alt=""
          width={107}
          height={70}
          unoptimized
          className={bannerImageStyles}
        />
        <div className={bannerTextWrapperStyles}>
          <span className={bannerLabelStyles}>{SPOTLIGHT_LABEL}</span>
          <div className={bannerBodyStyles}>
            <StructuredContentRenderer doc={details} />
          </div>
        </div>
      </div>
    </div>
  );
};
